import React, { useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { startNewOrder } from './actions';
import OrderInformation from './OrderInformation';
import { ORDER_STEPS } from './constants';
import { notifyToSlack } from 'common/gtmScripts';

import './order.css';
import './PaidSuccessResult-style.css';

// Answers asked on this screen. When a reused brief already has all of
// them, there is nothing to ask.
const AFTER_PAYMENT_ANSWERS = [
    'gameCategories',
    'targetAge',
    'swearing',
    'targetGender',
    'messagesAddress',
    'isSpecialCharacter',
    'isAdaptation'
];

const hasEveryAnswer = info =>
    !!info &&
    AFTER_PAYMENT_ANSWERS.every(
        key => info[key] !== undefined && info[key] !== null
    );

const DoneIcon = () => (
    <svg
        className="paid-icon"
        aria-hidden="true"
        width="56"
        height="56"
        viewBox="0 0 56 56"
        fill="none"
    >
        <circle cx="28" cy="28" r="28" fill="#1E7E89" />
        <path
            d="M17 28.5l7.5 7.5L39 21.5"
            stroke="#fff"
            strokeWidth="3.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

// Local preview (npm start, ?preview=order-placed): sample brief, and
// "saving" only logs to the browser console. Nothing reaches the server.
const PREVIEW_INFO = {
    version: 2,
    gameTitle: 'Preview game',
    isTranslateGameTitle: 'follow',
    platforms: ['PC'],
    isStringLengthLimit: false,
    isRemainTerms: false
};
const previewSave = () =>
    new Promise(resolve => {
        setTimeout(() => resolve({ data: {} }), 400);
    });

const PaidSuccessResult = ({ preview, startNewOrder, userEmail, ...props }) => {
    const orderId = preview ? 'preview' : props.orderId;
    const info = preview ? PREVIEW_INFO : props.info;
    const plainInfo = info && info.toJS ? info.toJS() : info;
    const [status, setStatus] = useState(
        !orderId || hasEveryAnswer(plainInfo) ? 'done' : 'brief'
    );

    let note = 'We’ll get started right away. Your receipt is on its way.';
    if (status === 'saved') {
        note = 'Thanks for the extra details. Your linguists will use them.';
    } else if (status === 'done') {
        note = 'We’ll get started right away.';
    }

    const isBrief = status === 'brief';
    // The questions stay mounted after "Skip for now", only hidden, so a
    // customer who skipped by mistake gets back every answer and the same
    // part with one click.
    const keepForm = status === 'brief' || status === 'skipped';

    return (
        <div className="order-main paid-successfully">
            <div className={`paid-page${keepForm ? ' paid-page-split' : ''}`}>
                {preview && (
                    <p className="paid-preview-note">
                        Preview only. Nothing is saved.
                    </p>
                )}

                <div className={`paid-split${isBrief ? '' : ' is-single'}`}>
                    {/* Right column on desktop, stays in view while the
                        customer scrolls. A short strip on top on phones. */}
                    <aside className="paid-aside">
                        <div className="paid-aside-card">
                            <div className="paid-confirm" role="status">
                                <DoneIcon />
                                <div>
                                    <h1 className="paid-title">
                                        Your order is placed
                                    </h1>
                                    <p className="paid-note">{note}</p>
                                </div>
                            </div>
                            {isBrief && (
                                <Link
                                    className="paid-link paid-aside-link"
                                    to={`/orders`}
                                    onClick={startNewOrder}
                                >
                                    View your orders
                                </Link>
                            )}
                            {status === 'skipped' && (
                                <div className="paid-resume">
                                    <p className="paid-resume-text">
                                        Skipped the questions? You can still
                                        answer them. Nothing you picked is lost.
                                    </p>
                                    <button
                                        type="button"
                                        className="paid-btn-outline"
                                        onClick={() => setStatus('brief')}
                                    >
                                        Answer the questions
                                    </button>
                                </div>
                            )}
                        </div>
                        {!isBrief && (
                            <div className="paid-actions">
                                <button
                                    type="button"
                                    className="paid-btn"
                                    onClick={startNewOrder}
                                >
                                    Start a new order
                                </button>
                                <Link
                                    className="paid-link"
                                    to={`/orders`}
                                    onClick={startNewOrder}
                                >
                                    View your orders
                                </Link>
                            </div>
                        )}
                    </aside>
                    {keepForm && (
                        <div className="paid-main" hidden={!isBrief}>
                            <OrderInformation
                                phase="after"
                                orderId={orderId}
                                info={info}
                                previewSave={preview ? previewSave : undefined}
                                onFinish={(how, counts) => {
                                    // One Slack line when the customer is
                                    // finished or skips. Never in the preview.
                                    if (!preview) {
                                        const answered = counts
                                            ? counts.answered
                                            : null;
                                        notifyToSlack({
                                            stepName: ORDER_STEPS.AFTER_PAYMENT,
                                            status:
                                                how === 'done'
                                                    ? 'did'
                                                    : 'skipped',
                                            userEmail,
                                            orderId,
                                            note:
                                                answered === null
                                                    ? ''
                                                    : `answered ${answered} of ${counts.total}`
                                        });
                                    }
                                    setStatus(
                                        how === 'done' ? 'saved' : 'skipped'
                                    );
                                }}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const mapStateToProps = state => ({
    orderId: state.getIn(['order', 'id']),
    userEmail: state.getIn(['auth', 'user', 'email']),
    info: state.getIn(['order', 'info', 'data'])
});

const mapDispatchToProps = (dispatch, ownProps) => {
    return bindActionCreators({ startNewOrder }, dispatch);
};

export default connect(mapStateToProps, mapDispatchToProps)(PaidSuccessResult);
